/**
 * FAQ Composable - Provides access to FAQs data
 * Uses the GO Commerce GraphQL Cache system for optimized caching
 */
import type { FAQType } from '@/types';
import { executeGraphQLQuery } from '../utils/graphqlCache';

export function useFaqs() {
  const mainStore = useMainStore();
  
  // Initialize with data from store if available
  const initialFaqs = mainStore.getFaqs || null;
  const faqs = useState<FAQType[] | null>("faqs", () => initialFaqs);
  const isLoading = ref(false);
  
  // GraphQL query for FAQs
  const FAQS_QUERY = `
    query GetFAQs {
      faqs(pagination: { limit: 500 }) {
        documentId
        content
        slug
        headline
        faqs_category {
          documentId
          title
          slug
          orderNo
        }
        orderNo
        createdAt
        updatedAt
        publishedAt
      }
    }
  `;

  /**
   * Fetch FAQs using the GO Commerce GraphQL Cache system
   * The GraphQL cache handles caching with proper TTLs
   * @param forceFresh Force a fresh fetch, ignoring cached data
   */
  const fetchFaqs = async (forceFresh = false) => {
    // Check store first if we have fresh data
    const storeFaqs = mainStore.getFaqs;
    if (storeFaqs && storeFaqs.length > 0 && !forceFresh) {
      faqs.value = storeFaqs;
      return storeFaqs;
    }

    // Prevent multiple concurrent API calls
    if (isLoading.value) {
      // Wait for the current fetch to complete
      await new Promise<void>(resolve => {
        const checkLoading = () => {
          if (!isLoading.value) {
            resolve();
          } else {
            setTimeout(checkLoading, 50);
          }
        };
        checkLoading();
      });
      return faqs.value;
    }

    try {
      isLoading.value = true;
      
      // Define the response type
      type ResponseType = {
        faqs: FAQType[];
      };
      
      // Execute the GraphQL query using the GO Commerce GraphQL Cache system
      // It automatically handles caching with TTLs and prevents duplicate requests
      const result = await executeGraphQLQuery<ResponseType>({
        query: FAQS_QUERY,
        operationName: 'GetFAQs'
      }, 60 * 60 * 1000, forceFresh); // 1 hour cache TTL, or force fresh if requested
      
      if (result?.faqs) {
        // Store in local state and main store
        faqs.value = result.faqs;
        mainStore.setFaqs(result.faqs);
        console.log('[useFaqs] Fetched FAQs data ' + (forceFresh ? '(forced fresh)' : '(from cache)'));
        return result.faqs;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    faqs,
    isLoading: readonly(isLoading),
    fetchFaqs,
  };
}
